import type { AuthenticationQueryParams } from '@dtos/query-params';
import { SendPinCodeResult } from '@models/send-pin-code-result';
import { IPinCodeService } from '@services/pin-code-service';
import {
    action,
    computed,
    observable
} from 'mobx';

export interface IPinCodeStore {
    readonly pinCodeResult: SendPinCodeResult | null;
    readonly sentInitialPinRequest: boolean;
    readonly hasInvalidId: boolean;
    readonly pinCodeInput: string;
    readonly didSendSuccessfully: boolean;
    readonly pinCodeRequests: number;
    readonly pinCodeResponses: number;
    readonly hadErrorSendingPinCode: boolean;

    sendPinCodeToWorker(params: AuthenticationQueryParams): Promise<void>;
    sendInitialPinCode(params: AuthenticationQueryParams): Promise<void>;
    editPinCodeInput(input: string): void;
    reset(): void;
}

export class PinCodeStore implements IPinCodeStore {
    @observable
    pinCodeResult: SendPinCodeResult | null = null;

    @observable
    pinCodeInput: string = '';

    @observable
    pinCodeRequests: number = 0;

    @observable
    pinCodeResponses: number = 0;

    constructor(
        // @ts-ignore
        private pinCodeService: IPinCodeService
    ) {
    }

    @computed
    get hadErrorSendingPinCode(): boolean {
        return this.hasInvalidId ||
            this.pinCodeResult === SendPinCodeResult.UnrecognisedError;
    }

    @computed
    get hasInvalidId(): boolean {
        return this.pinCodeResult === SendPinCodeResult.InvalidId;
    }

    @computed
    get didSendSuccessfully(): boolean {
        return this.pinCodeResult === SendPinCodeResult.Success;
    }

    @computed
    get sentInitialPinRequest(): boolean {
        return this.pinCodeRequests >= 1;
    }

    @action
    async sendPinCodeToWorker(params: AuthenticationQueryParams): Promise<void> {
        this.pinCodeRequests += 1;

        if (!params.authorizedRepresentativeId) {
            this.pinCodeResult = SendPinCodeResult.InvalidId;
            return;
        }

        try {
            this.pinCodeResult = await this.pinCodeService.sendRepresentativePinCodeToWorker({
                authorizedRepresentativeId: params.authorizedRepresentativeId
            });
        } catch (error) {
            // Could be errors when requesting the guest token for the endpoint
            this.pinCodeResult = SendPinCodeResult.UnrecognisedError;
            return;
        }

        this.pinCodeResponses += 1;
    }

    @action
    async sendInitialPinCode(params: AuthenticationQueryParams): Promise<void> {
        if (this.sentInitialPinRequest) {
            return;
        }

        await this.sendPinCodeToWorker(params);
    }

    @action
    editPinCodeInput(input: string) {
        this.pinCodeInput = input;
    }

    @action
    reset() {
        this.pinCodeResult = null;
        this.pinCodeInput = '';
        this.pinCodeRequests = 0;
    }
}

export default PinCodeStore;



// WEBPACK FOOTER //
// ./src/stores/authorisation/pin-code-store.ts